import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import PasswordInput from '../../components/settings/components/PasswordInput';
import SocialLoginButtons from './components/SocialLoginButtons';
import { useRegisterMutation } from '../../redux/apis/endpoints/authApi';
import { setRegistrationSuccess } from '../../redux/slices/authSlice';

const Register = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const userLang = navigator.language || navigator.userLanguage;
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    general: ''
  });
  const [successMessage, setSuccessMessage] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [passwordValidationErrors, setPasswordValidationErrors] = useState([]);
  const [userEmail, setUserEmail] = useState('');

  // RTK Query hook for registration
  const [register, { isLoading, error: registerError }] = useRegisterMutation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update error state when RTK Query error changes
  useEffect(() => {
    if (registerError) {
      // Check for specific email taken error
      if (registerError.data?.message && Array.isArray(registerError.data.message)) {
        const messageObj = registerError.data.message[0]?.messages?.[0];
        if (messageObj?.id === "Auth.form.error.email.taken") {
          setErrors(prev => ({ 
            ...prev, 
            email: 'This email is already registered. Please try logging in or use a different email.'
          }));
          return;
        }
      }
      
      // Check for email taken in data array
      if (registerError.data?.data && Array.isArray(registerError.data.data)) {
        const messageObj = registerError.data.data[0]?.messages?.[0];
        if (messageObj?.id === "Auth.form.error.email.taken") {
          setErrors(prev => ({
            ...prev,
            email: 'This email is already registered. Please try logging in or use a different email.'
          }));
          return;
        }
      }
      
      // For other error types, set as general error
      setErrors(prev => ({
        ...prev,
        general: registerError.data?.error?.message || 
                registerError.data?.message || 
                'Registration failed. Please try again.'
      }));
    }
  }, [registerError]);

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear specific field error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear general error when any field changes
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
    
    // Live validation for email format
    if (name === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else if (name === 'email' && value && validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      general: ''
    };
    let isValid = true;
    
    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
      isValid = false;
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
      isValid = false;
    }
    
    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    
    // Check password strength validation errors
    if (passwordValidationErrors.length > 0) {
      newErrors.password = 'Please fix the password validation errors';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const result = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password
      }).unwrap();
      
      // Check if registration was successful
      if (result && result.user) {
        // Set registration success in Redux
        dispatch(setRegistrationSuccess(true));
        
        // Show success message - we don't need to send email manually
        // Strapi automatically handles the confirmation email flow:
        // 1. Creates the user with "confirmed: false" status
        // 2. Generates a confirmation token 
        // 3. Calls our overridden sendConfirmationEmail function
        // 4. Our function uses the SendGrid template
        setSuccessMessage('Registration successful! Please check your email to confirm your account.');
        
        // Store the user email for resend verification
        setUserEmail(result.user.email);
        
        // Clear form
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
        
        // Store the email for potential resend
        // We don't automatically redirect anymore
      } else {
        // Handle unexpected success response format
        setErrors(prev => ({
          ...prev,
          general: 'Registration completed but with an unexpected response. Please contact support if you don\'t receive a verification email.'
        }));
        console.warn('Unexpected registration response format:', result);
      }
    } catch (err) {
      // Error handling is done in the useEffect hook
      console.error('Registration error details:', err);
    }
  };

  const handlePasswordValidationChange = (errors) => {
    setPasswordValidationErrors(errors);
  };

  // Check if this is a closet user from URL params
  const urlParams = new URLSearchParams(location.search);
  const isClosetUser = urlParams.get('isClosetUser');

  // Determine background style
  const backgroundStyle = isClosetUser
    ? {
        minHeight: '100vh',
        backgroundImage:
          "url('https://res.cloudinary.com/sheyou/image/upload/v1727875894/virtual_closet_5d66ccc54e.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        minHeight: '100vh'
      };

  // Common input style for consistency across all inputs
  const inputStyle = "text-gray-900 placeholder:text-gray-400";

  const handleResendVerification = () => {
    history.push('/auth/check-email', { 
      email: userEmail || ''
    });
  };

  return (
    <div className="gradient_limeade flex flex-col" style={backgroundStyle}>
      <div className="flex flex-grow items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Create your account
            </h2>
          </div>
          
          {errors.general && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{errors.general}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex flex-col">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{successMessage}</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">Didn't receive email?</p>
                  <Button
                    type="button"
                    onClick={handleResendVerification}
                    className="mt-2 w-full bg-brand-teal hover:bg-brand-teal-dark"
                  >
                    Resend Verification
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {!successMessage &&  <form className="mt-8 space-y-6" onSubmit={handleRegister}>
            <div className="space-y-4">
              <FormInput
                label="Username"
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                fluid={true}
                value={formData.username}
                onChange={handleChange}
                placeholder="Username"
                leftIcon={<UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                className={inputStyle}
                disabled={isLoading}
                error={errors.username}
              />
              
              <FormInput
                label="Email address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                fluid={true}
                value={formData.email}
                onChange={handleChange}
                placeholder="Email address"
                leftIcon={<EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                className={inputStyle}
                disabled={isLoading}
                error={errors.email}
              />
              
              <PasswordInput
                label="Password"
                value={formData.password}
                onChange={(e) => handleChange({ target: { name: 'password', value: e.target.value } })}
                name="password"
                id="password"
                autoComplete="new-password"
                required
                placeholder="Password"
                showValidation={true}
                userLang={userLang}
                onValidationChange={handlePasswordValidationChange}
                className={inputStyle}
                disabled={isLoading}
                error={errors.password}
              />
              
              <PasswordInput
                label="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange({ target: { name: 'confirmPassword', value: e.target.value } })}
                name="confirmPassword"
                id="confirmPassword"
                autoComplete="new-password"
                required
                placeholder="Confirm password"
                userLang={userLang}
                className={inputStyle}
                disabled={isLoading}
                error={errors.confirmPassword}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <Button
                type="submit"
                loading={isLoading}
                className="w-full bg-brand-teal hover:bg-brand-teal-dark"
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Sign up'}
              </Button>
              
              <SocialLoginButtons redirectTo="/dashboard" disabled={isLoading} />

              <div className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className={`font-medium text-brand-teal hover:text-brand-teal-light ${isLoading ? 'pointer-events-none opacity-50' : ''}`}
                >
                  Sign in
                </Link>
              </div>
            </div>
          </form>}
        </div>
      </div>
    </div>
  );
};

export default Register; 